import { useSyncExternalStore } from 'react';
import { ADAPTERS, pickLightModel } from '@/llm';
import { describeError } from '@/llm/types';
import { newId } from '@/lib/bubbles';
import { coverage, loadAskLog, pickGaps, recordAsk, saveAskLog, type AskLog } from '@/memory/curiosity';
import { applyExtraction, buildExtractionMessages, EXTRACTION_SYSTEM, parseExtraction } from '@/memory/extract';
import { related, retrieve } from '@/memory/retrieve';
import { buildSystemPrompt, dateStamp, openerInstruction, toChatMessages } from '@/prompt/assemble';
import { activeLLM, settingsStore } from '@/settings/settingsStore';
import { repo } from '@/storage/db';
import type { JournalEntry, Memory, Message } from '@/types';

export interface AppState {
  ready: boolean;
  messages: Message[];
  memories: Memory[];
  journal: JournalEntry[];
  /** The reply being streamed, before it is stored as a message. */
  streaming: string | null;
  busy: boolean;
  learning: boolean;
  notice: string | null;
  /** Last extraction result, for the memory page to show what it just learned. */
  lastLearned: number;
}

let state: AppState = { ready: false, messages: [], memories: [], journal: [], streaming: null, busy: false, learning: false, notice: null, lastLearned: 0 };
const listeners = new Set<() => void>();
let askLog: AskLog = loadAskLog();
let abort: AbortController | null = null;
let noticeTimer: ReturnType<typeof setTimeout> | undefined;

function set(patch: Partial<AppState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

const RETURN_AFTER_MS = 6 * 60 * 60 * 1000;
const OPENER_SESSION_KEY = 'puppy.openerAt';
// The one-time offer to hear about her life in one go comes after a few exchanges, only while it knows nobody in it.
const INVITE_AFTER_USER_MESSAGES = 5;

function recentUserMessages(n = 3): Message[] {
  return state.messages.filter((m) => m.role === 'user').slice(-n);
}

export const appStore = {
  get: () => state,
  subscribe(l: () => void) {
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  },

  async init() {
    const data = await repo.loadAll();
    set({ ...data, ready: true });
  },

  notify(message: string) {
    clearTimeout(noticeTimer);
    set({ notice: message });
    noticeTimer = setTimeout(() => set({ notice: null }), 6000);
  },

  /** She opened the app: speak first when it is the first meeting or she has been away a while. Once per session. */
  async openIfNeeded() {
    const s = settingsStore.get();
    if (!s.companionName || !activeLLM(s) || state.busy) return;
    const last = state.messages[state.messages.length - 1];
    const kind = !last ? 'first' : Date.now() - last.at > RETURN_AFTER_MS ? 'return' : null;
    if (!kind) return;
    try {
      if (sessionStorage.getItem(OPENER_SESSION_KEY)) return;
      sessionStorage.setItem(OPENER_SESSION_KEY, String(Date.now()));
    } catch {
      // no sessionStorage: allow it
    }
    const hours = last ? (Date.now() - last.at) / 3600000 : 0;
    // A return opener is the cheapest place for one small question: it has to say something anyway.
    const curious = kind === 'return' ? pickGaps({ memories: state.memories, text: '', recentUser: recentUserMessages(), log: askLog, now: Date.now(), limit: 1 }) : null;
    await this.reply(openerInstruction(kind, hours, s, curious?.gaps[0]?.label), null);
  },

  async send(text: string) {
    const clean = text.trim();
    if (!clean || state.busy) return;
    const user: Message = { id: newId(), role: 'user', text: clean, at: Date.now() };
    set({ messages: [...state.messages, user] });
    await repo.putMessage(user);
    await this.reply(null, user);
  },

  /**
   * Produces one assistant message. `instruction` is an unstored steering turn (an opener); `user` is the stored
   * message being answered. The query for memory retrieval is whichever exists.
   */
  async reply(instruction: string | null, user: Message | null) {
    const s = settingsStore.get();
    const llm = activeLLM(s);
    if (!llm) {
      this.notify('还没有配置模型的 Key，去设置里填一个。');
      return;
    }
    const query = user?.text ?? state.messages.slice(-3).map((m) => m.text).join(' ');
    const now = Date.now();
    const retrieved = retrieve(state.memories, query, now);
    const curiosity = user ? pickGaps({ memories: state.memories, text: user.text, recentUser: recentUserMessages(), log: askLog, now }) : undefined;
    const userCount = state.messages.filter((m) => m.role === 'user').length;
    const invite = !!user && !s.invitedAt && userCount >= INVITE_AFTER_USER_MESSAGES && coverage(state.memories).people === 0;
    if (invite) settingsStore.markInvited();
    const system = buildSystemPrompt({ identity: s, retrieved, journal: state.journal, now, curiosity, invite });
    const history = toChatMessages(state.messages);
    if (instruction) history.push({ role: 'user', content: [{ type: 'text', text: instruction }] });
    if (history.length === 0) history.push({ role: 'user', content: [{ type: 'text', text: '（她打开了对话）' }] });

    abort = new AbortController();
    set({ busy: true, streaming: '' });
    let text = '';
    let error: string | undefined;
    try {
      text = await ADAPTERS[llm.provider].chat({ system, messages: history, model: llm.model, apiKey: llm.apiKey, signal: abort.signal, onDelta: (d) => set({ streaming: (state.streaming ?? '') + d }) });
    } catch (e) {
      error = describeError(e);
      text = state.streaming ?? '';
    }
    abort = null;
    const assistant: Message = { id: newId(), role: 'assistant', text: text.trim(), at: Date.now(), model: `${llm.provider}/${llm.model}`, ...(error ? { error } : {}) };
    // A failed opener leaves no trace (and may try again next time); a failed answer stays so she sees why.
    if (assistant.text || (error && user)) {
      set({ messages: [...state.messages, assistant], streaming: null, busy: false });
      await repo.putMessage(assistant);
    } else {
      set({ streaming: null, busy: false });
      if (error && !user) {
        try {
          sessionStorage.removeItem(OPENER_SESSION_KEY);
        } catch {
          // ignore
        }
      }
    }
    if (error) this.notify(error);
    // Touch the notes it just used so the memory page can show what it leaned on.
    const used = [...retrieved.relevant, ...retrieved.followups];
    if (used.length) {
      const touched = used.map((m) => ({ ...m, lastRecalledAt: now }));
      set({ memories: state.memories.map((m) => touched.find((t) => t.id === m.id) ?? m) });
      void repo.putMemories(touched);
    }
    if (!error && user && s.memoryEnabled) void this.learn(user, assistant);
  },

  stop() {
    abort?.abort();
  },

  /** Background: read the exchange and write down what is worth keeping. Failures are silent by design. */
  async learn(user: Message, assistant: Message) {
    const s = settingsStore.get();
    const llm = activeLLM(s);
    if (!llm) return;
    const idx = state.messages.findIndex((m) => m.id === user.id);
    const context = state.messages.slice(Math.max(0, idx - 2), idx).filter((m) => !m.error);
    const exchange = [...context, user, assistant].map((m) => ({ role: m.role, text: m.text }));
    const now = Date.now();
    const today = dateStamp(now);
    const input = {
      userName: s.userName,
      companionName: s.companionName,
      now,
      exchange,
      related: related(state.memories, `${user.text} ${assistant.text}`),
      todayJournal: state.journal.find((j) => j.date === today)?.text,
    };
    set({ learning: true });
    try {
      const model = pickLightModel(llm.models, llm.model);
      const raw = await ADAPTERS[llm.provider].chat({ system: EXTRACTION_SYSTEM, messages: buildExtractionMessages(input), model, apiKey: llm.apiKey, jsonMode: true });
      const ex = parseExtraction(raw);
      const changed = applyExtraction(state.memories, ex, [user.id, assistant.id], now, newId);
      if (changed.length) {
        const byId = new Map(changed.map((m) => [m.id, m]));
        const merged = [...state.memories.map((m) => byId.get(m.id) ?? m), ...changed.filter((m) => !state.memories.some((e) => e.id === m.id))];
        set({ memories: merged, lastLearned: now });
        await repo.putMemories(changed);
      }
      if (ex.journal) {
        const entry: JournalEntry = { date: today, text: ex.journal, updatedAt: now };
        set({ journal: [...state.journal.filter((j) => j.date !== today), entry].sort((a, b) => a.date.localeCompare(b.date)) });
        await repo.putJournal(entry);
      }
      if (ex.asked.length || ex.declined.length) {
        askLog = recordAsk(askLog, ex.asked, ex.declined, now);
        saveAskLog(askLog);
      }
    } catch {
      // it still talked; it just did not learn from this one
    } finally {
      set({ learning: false });
    }
  },

  async putMemory(m: Memory) {
    const exists = state.memories.some((e) => e.id === m.id);
    set({ memories: exists ? state.memories.map((e) => (e.id === m.id ? m : e)) : [...state.memories, m] });
    await repo.putMemory(m);
  },
  async deleteMemory(id: string) {
    set({ memories: state.memories.filter((m) => m.id !== id) });
    await repo.deleteMemory(id);
  },
  async deleteMessage(id: string) {
    set({ messages: state.messages.filter((m) => m.id !== id) });
    await repo.deleteMessage(id);
  },

  askLog: () => askLog,

  async clearAll() {
    await repo.clearAll();
    set({ messages: [], memories: [], journal: [] });
    askLog = { asked: {}, declined: {} };
    saveAskLog(askLog);
    try {
      sessionStorage.removeItem(OPENER_SESSION_KEY);
    } catch {
      // ignore
    }
  },

  exportData() {
    return { version: 1, exportedAt: Date.now(), settings: settingsStore.get(), messages: state.messages, memories: state.memories, journal: state.journal };
  },
  async importData(data: { settings?: unknown; messages?: Message[]; memories?: Memory[]; journal?: JournalEntry[] }) {
    const next = { messages: data.messages ?? [], memories: data.memories ?? [], journal: data.journal ?? [] };
    await repo.replaceAll(next);
    if (data.settings && typeof data.settings === 'object') settingsStore.replace(data.settings as ReturnType<typeof settingsStore.get>);
    set(next);
  },
};

export function useAppState(): AppState {
  return useSyncExternalStore(appStore.subscribe, appStore.get, appStore.get);
}
